// Lot 678 — lecture DIFFÉRÉE d'un aperçu vidéo (carte LIVE du profil et de la tête du fil, lots 668-669).
// Avant : le lecteur muet tournait en permanence dès le montage de la carte, même hors écran ou app en arrière-plan
// (téléchargement + décodage continus = la fonctionnalité la plus coûteuse de la 2.4). Maintenant la carte montre
// l'image du direct et le lecteur ne démarre que si :
//   • la carte est visible à ≥ 60 % pendant `delayMs` (un défilement rapide ne lance rien),
//   • l'onglet / l'app est au premier plan,
//   • pas de lien 2G ni d'économiseur de données (sinon image seule : un tap ouvre le direct, avec le son).
// Il s'arrête (lecteur démonté → HLS / WebRTC libérés) dès que la carte quitte l'écran ou que l'app passe en arrière-plan.
import { useEffect, useState } from "react";
import { dataSaverActive } from "@/lib/mediaUrl";
import { isSlowNetwork } from "@/lib/videoSource";

export const PREVIEW_DELAY_MS = 800;
export const PREVIEW_THRESHOLD = 0.6;
export const previewNetworkOk = () => !dataSaverActive() && !isSlowNetwork();

export const useDeferredAutoplay = (ref, { enabled = true, delayMs = PREVIEW_DELAY_MS } = {}) => {
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    const el = ref.current;
    // Garde-fou coupé, réseau à préserver ou navigateur sans IntersectionObserver → image seule, jamais de lecteur auto.
    if (!enabled || !el || !previewNetworkOk() || typeof IntersectionObserver === "undefined") { setPlaying(false); return undefined; }
    let timer = null;
    let visible = false;
    const stop = () => { if (timer) { clearTimeout(timer); timer = null; } setPlaying(false); };
    const arm = () => {
      if (timer || document.visibilityState === "hidden") return;
      timer = setTimeout(() => { timer = null; setPlaying(true); }, delayMs);
    };
    const io = new IntersectionObserver(([entry]) => {
      visible = !!entry && entry.isIntersecting && entry.intersectionRatio >= PREVIEW_THRESHOLD;
      if (visible) arm(); else stop();
    }, { threshold: [0, PREVIEW_THRESHOLD] });
    io.observe(el);
    // App / onglet en arrière-plan → arrêt immédiat ; retour au premier plan → redémarre après le délai si toujours visible.
    const onVis = () => { if (document.visibilityState === "hidden") stop(); else if (visible) arm(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { io.disconnect(); document.removeEventListener("visibilitychange", onVis); stop(); };
  }, [ref, enabled, delayMs]);
  return playing;
};
